'use client';

import { useMemo } from 'react';

import type { TableColumnsDisplayConfig } from '../shared/table-columns-display';
import type { ClientsTableColumn } from './utils/clients-columns';
import { TableColumnsDisplay } from '../shared/table-columns-display';
import {
  DEFAULT_CLIENTS_TABLE_COLUMNS,
  getDefaultClientColumns,
  setDefaultClientColumns,
} from './utils/clients-columns';

const MAX_SELECTABLE_COLUMNS = 5;
const REQUIRED_COLUMNS: readonly ClientsTableColumn[] = ['Household'] as const;

type DisplayOption = Exclude<ClientsTableColumn, 'Household'>;

const ORDERED_DISPLAY_OPTIONS: readonly DisplayOption[] = [
  'Billable Assets',
  'ARR',
  'Service Calendar',
  'Next Service Item',
  'Last Contact',
  'Comments',
  'Primary',
  'Owner',
  'Tags',
] as const;

function columnsToDisplayOptions(columns: readonly ClientsTableColumn[]): DisplayOption[] {
  const set = new Set(columns);
  return ORDERED_DISPLAY_OPTIONS.filter((opt) => set.has(opt));
}

function displayOptionsToColumns(options: readonly DisplayOption[]): ClientsTableColumn[] {
  const ordered = ORDERED_DISPLAY_OPTIONS.filter((opt) => options.includes(opt));
  return ['Household', ...ordered];
}

function ensureRequiredColumns(columns: readonly ClientsTableColumn[]): ClientsTableColumn[] {
  if (columns.includes('Household')) {
    return Array.from(columns);
  }
  return ['Household', ...columns];
}

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly anchorEl: HTMLElement | null;
  readonly selectedColumns: readonly ClientsTableColumn[];
  readonly onColumnsChange: (columns: ClientsTableColumn[]) => void;
  readonly allowPrimary: boolean;
  readonly allowOwner: boolean;
  readonly allowArr: boolean;
  readonly allowNextServiceItem: boolean;
};

export function ClientsColumnsDisplay({
  open,
  onClose,
  anchorEl,
  selectedColumns,
  onColumnsChange,
  allowPrimary,
  allowOwner,
  allowArr,
  allowNextServiceItem,
}: Props) {
  const filteredDisplayOptions = useMemo(() => {
    return ORDERED_DISPLAY_OPTIONS.filter((opt) => {
      if (opt === 'Primary' && !allowPrimary) return false;
      if (opt === 'Owner' && !allowOwner) return false;
      if (opt === 'ARR' && !allowArr) return false;
      if (opt === 'Next Service Item' && !allowNextServiceItem) return false;
      return true;
    });
  }, [allowPrimary, allowOwner, allowArr, allowNextServiceItem]);

  const config: TableColumnsDisplayConfig<ClientsTableColumn, DisplayOption> = useMemo(
    () => ({
      displayOptions: filteredDisplayOptions,
      displayOptionToColumns: displayOptionsToColumns,
      columnsToDisplayOptions,
      requiredColumns: REQUIRED_COLUMNS,
      ensureRequiredColumns,
      maxSelectable: MAX_SELECTABLE_COLUMNS,
      maxTotalColumns: MAX_SELECTABLE_COLUMNS + REQUIRED_COLUMNS.length, // 5 selectable + 1 required = 6
      title: 'Display properties',
      countLabel: 'Properties',
      limitWarningMessage: `You've reached the ${MAX_SELECTABLE_COLUMNS}-property limit.`,
      includeRequiredInCount: false, // Don't include 'Household' in the count
      getDefaultColumns: () =>
        getDefaultClientColumns({
          includePrimary: allowPrimary,
          includeOwner: allowOwner,
          includeArr: allowArr,
          includeNextServiceItem: allowNextServiceItem,
        }),
      getHardcodedDefaults: () => DEFAULT_CLIENTS_TABLE_COLUMNS.filter((c) => allowArr || c !== 'ARR'),
      setDefaultColumns: setDefaultClientColumns,
      selectedColumns,
      onColumnsChange,
    }),
    [
      filteredDisplayOptions,
      allowPrimary,
      allowOwner,
      allowArr,
      allowNextServiceItem,
      selectedColumns,
      onColumnsChange,
    ],
  );

  return <TableColumnsDisplay open={open} onClose={onClose} anchorEl={anchorEl} config={config} />;
}
